import { DateTime } from 'luxon';
import { PersonCardDailyColumn, Severity, UserAlertEntry } from '../models';
import { companyChartWeekdays, elapsedChartWeekdays } from './person-week-evidence-detail';
import {
  CardSeverity, FiredSignal, formatPersonSignalValue, PERSON_SIGNAL_TYPES, personSignalTypeShortLabel, PersonSignalType, WEEKDAY_ORDER
} from './person-signal-card';

/**
 * AA-214 — turns raw per-user alert entries into FiredSignal[] + the daily chart. The backend
 * doesn't send per-day `day` values yet, so each type builds from real per-day points when
 * available (buildFiredSignalFromDayPoints) and falls back to a range-aggregate estimate
 * (buildFiredSignalFromRangeAggregate) otherwise, picking up real per-day data automatically once
 * the backend starts sending it.
 */

/** Narrows to the five in-scope person-card types; unrecognized alertIds (e.g. attendance_issues)
 * return null rather than being guessed at. */
export function personSignalTypeFor(alertId: string): PersonSignalType | null {
  return (PERSON_SIGNAL_TYPES as readonly string[]).includes(alertId) ? (alertId as PersonSignalType) : null;
}

// Backend sends full lowercase weekday names; the rest of this feature uses short Mon..Sun form.
const FULL_TO_SHORT_WEEKDAY: Record<string, string> = {
  monday: 'Mon', tuesday: 'Tue', wednesday: 'Wed', thursday: 'Thu', friday: 'Fri', saturday: 'Sat', sunday: 'Sun',
};

function weekdayOrderIndex(shortDay: string): number {
  const idx = WEEKDAY_ORDER.indexOf(shortDay);
  return idx < 0 ? WEEKDAY_ORDER.length : idx;
}

// Short weekday of every local calendar day in [fromIso, toIso). from/to are UTC instants of the
// org's local midnights (DateRange.asApiParams()), so reading their UTC dates would shift the whole
// range a day for any non-UTC zone.
function weekdaysInRange(fromIso: string, toIso: string, timezone: string | undefined): string[] {
  const zone = timezone || 'utc';
  const end = DateTime.fromISO(toIso, { zone });
  let cursor = DateTime.fromISO(fromIso, { zone }).startOf('day');
  if (!cursor.isValid || !end.isValid) return [];
  const days: string[] = [];
  for (; cursor < end; cursor = cursor.plus({ days: 1 })) days.push(WEEKDAY_ORDER[cursor.weekday - 1]);
  return days;
}

// Last chart day on or before toIso's exclusive end (the company's two off days roll back) — the
// fallback's peakDay anchor: a real date inside the range, but not a claim that it was really the
// peak day.
function lastChartDayOnOrBefore(toIso: string, timezone: string | undefined, chartDays: string[]): string {
  const lastDay = lastIncludedLocalDay(toIso, timezone);
  if (!lastDay.isValid) return chartDays[0];
  for (let back = 0; back < 7; back++) {
    const weekday = WEEKDAY_ORDER[lastDay.minus({ days: back }).weekday - 1]; // luxon weekday: Mon=1..Sun=7
    if (chartDays.includes(weekday)) return weekday;
  }
  return chartDays[0];
}

// Last real instant inside a range whose exclusive end is `toIso`, shared by mondayOfWeekContaining
// below and person-week-evidence.ts's future-day exclusion. Resolved in the org's own timezone, not
// raw UTC — a local-midnight boundary can land on a different UTC calendar day depending on the
// zone's offset, which would misidentify the range's actual last covered day.
export function lastIncludedLocalDay(toIso: string, timezone: string | undefined): DateTime {
  return DateTime.fromISO(toIso, { zone: timezone || 'utc' }).minus({ millisecond: 1 });
}

// Real ISO date for a Mon..Sun `offset` (0-6) from `weekStart` — shared by buildPersonCardDailyChart's
// elapsed-day filter below and person-week-evidence.ts's week matrix, so both agree on the same
// calendar dates for a given weekStart.
export function isoDateFor(weekStart: Date, offset: number): string {
  const date = new Date(weekStart);
  date.setUTCDate(date.getUTCDate() + offset);
  return date.toISOString().slice(0, 10);
}

/** Monday of the week containing lastIncludedLocalDay(toIso, timezone), used to turn a peakDay
 * weekday name back into a real date. A range spanning more than one week resolves every peakDay
 * against the range end's own week. */
export function mondayOfWeekContaining(toIso: string, timezone: string | undefined): Date {
  const lastIncludedDay = lastIncludedLocalDay(toIso, timezone);
  const monday = lastIncludedDay.minus({ days: lastIncludedDay.weekday - 1 }); // luxon weekday: Mon=1..Sun=7
  return new Date(Date.UTC(monday.year, monday.month - 1, monday.day));
}

/** First calendar date of the company week containing lastIncludedLocalDay(toIso, timezone) —
 * the chart's anchor, where mondayOfWeekContaining stays the anchor for links and View details.
 * `firstDayOfWeek` follows company settings (0 or 7 = Sunday, 1 = Monday … 6 = Saturday; anything
 * else falls back to Monday, as companyChartWeekdays does). */
export function companyWeekStartContaining(toIso: string, timezone: string | undefined, firstDayOfWeek = 1): Date {
  const lastIncludedDay = lastIncludedLocalDay(toIso, timezone);
  const startWeekday = firstDayOfWeek === 0 || firstDayOfWeek === 7 ? 7 : firstDayOfWeek >= 1 && firstDayOfWeek <= 6 ? firstDayOfWeek : 1;
  const start = lastIncludedDay.minus({ days: (lastIncludedDay.weekday - startWeekday + 7) % 7 }); // luxon weekday: Mon=1..Sun=7
  return new Date(Date.UTC(start.year, start.month - 1, start.day));
}

/** One alert type's value on one real day — shared by FiredSignal and the daily chart, and
 * exported for AA-225's week-matrix (person-week-evidence.ts), which pivots the same points by day. */
export interface DayPoint {
  day: string; // short weekday
  value: number;
  breaching: boolean; // true when severity !== 'ok'
  signalId: string;
}

// Only entries with a real, recognized `day` are used; day-less or unrecognized entries are
// excluded here rather than guessed at (buildFiredSignalsForUser falls back to the range-aggregate
// estimate when none exist for a type).
function dayPointsForType(entries: UserAlertEntry[]): DayPoint[] {
  return entries
    .map(e => {
      const day = FULL_TO_SHORT_WEEKDAY[e.day];
      return day ? { day, value: e.ratio, breaching: e.severity !== 'ok', signalId: e.signalId } : null;
    })
    .filter((point): point is DayPoint => point !== null)
    .sort((a, b) => weekdayOrderIndex(a.day) - weekdayOrderIndex(b.day));
}

/** Threshold-band config for one alert type's severity math, from activity/agent-gateway/alerts (AlertType.severity). */
export interface SignalBandConfig {
  threshold: number;
  x: number;
  y: number;
  direction?: 'above' | 'below';
}

/**
 * Builds one alert type's FiredSignal from its real per-day points. Returns null when this type
 * never breached this range or has no day-tagged points (caller falls back to
 * buildFiredSignalFromRangeAggregate).
 *
 * `d` is the week's weekday count, not points.length — some alert types (e.g. unusual_activity)
 * only ever send an entry on a day they fired, with no "ok" entry otherwise, so points.length
 * would misread "2 of 2 days" (100%) instead of the real "2 of 5". Weekend breaches are left out
 * of n/peak, since `d` (and View details' evaluated days) only cover the company's chart days.
 */
function buildFiredSignalFromDayPoints(
  type: PersonSignalType, points: DayPoint[], config: SignalBandConfig, d: number, chartDays: string[],
): FiredSignal | null {
  const fired = points.filter(point => point.breaching && chartDays.includes(point.day));
  if (!fired.length) return null;
  const peak = fired.reduce((max, point) => (point.value > max.value ? point : max), fired[0]);
  // Every breaching day's row, not just the peak's — dismiss must mark them all, or the type
  // re-fires from whichever row was left active.
  const signalIds = fired.map(point => point.signalId).filter(id => id.trim().length > 0);
  return {
    type, n: fired.length, d, threshold: config.threshold, peak: peak.value, peakDay: peak.day,
    direction: config.direction ?? 'above', x: config.x, y: config.y,
    signalIds, peakDayIsReal: true,
  };
}

const CARD_SEVERITY_VALUES: readonly CardSeverity[] = ['info', 'warning', 'critical'];

function toCardSeverity(severity: Severity | 'ok'): CardSeverity | null {
  return (CARD_SEVERITY_VALUES as readonly string[]).includes(severity) ? (severity as CardSeverity) : null;
}

/**
 * Fallback for a type with no day-tagged entries: the same range-aggregate estimate used before
 * real per-day data existed. `n` is pinned to 1 ("fired at least once" — never inflated to `d`,
 * which nothing here has evidence for). Also returns the backend's own severity for the peak
 * entry, since n=1 would otherwise cap severityForSignal at 'warning' even when the backend says
 * 'critical'.
 */
function buildFiredSignalFromRangeAggregate(
  type: PersonSignalType, entries: UserAlertEntry[], config: SignalBandConfig, d: number, peakDay: string,
): { signal: FiredSignal, severity: CardSeverity } | null {
  const fired = entries.filter(entry => entry.severity !== 'ok');
  if (!fired.length) return null;
  const peak = fired.reduce((max, entry) => (entry.ratio > max.ratio ? entry : max), fired[0]);
  const severity = toCardSeverity(peak.severity);
  if (!severity) return null;
  // Every fired entry's row, not just the peak's — same reasoning as buildFiredSignalFromDayPoints.
  const signalIds = fired.map(entry => entry.signalId).filter(id => id.trim().length > 0);
  return {
    signal: {
      type, n: 1, d, threshold: config.threshold, peak: peak.ratio, peakDay,
      direction: config.direction ?? 'above', x: config.x, y: config.y,
      signalIds, peakDayIsReal: false,
    },
    severity,
  };
}

export interface UserFiredSignals {
  signals: FiredSignal[];
  // Full daily series per type, fired or not — used by buildPersonCardDailyChart and AA-225's
  // week-matrix (person-week-evidence.ts). Absent for a range-aggregate-fallback-built type.
  dailyPointsByType: Partial<Record<PersonSignalType, DayPoint[]>>;
  // Severity override for range-aggregate-fallback types (see buildFiredSignalFromRangeAggregate);
  // real per-day types compute their own severity instead.
  precomputedSeverities: Partial<Record<PersonSignalType, CardSeverity>>;
}

/**
 * Turns one user's raw entries into FiredSignal[] plus a precomputedSeverities override for
 * fallback-built types. Entries outside PERSON_SIGNAL_TYPES or with no resolved band config are
 * dropped rather than guessed at.
 */
export function buildFiredSignalsForUser(
  entries: UserAlertEntry[], bandConfigByAlertId: Record<string, SignalBandConfig>, dateRange: { from: string, to: string },
  timezone?: string, firstDayOfWeek = 1,
): UserFiredSignals {
  const chartDays = companyChartWeekdays(firstDayOfWeek);
  const rangeDays = weekdaysInRange(dateRange.from, dateRange.to, timezone);
  const d = Math.max(1, rangeDays.filter(day => chartDays.includes(day)).length);
  const fallbackPeakDay = lastChartDayOnOrBefore(dateRange.to, timezone, chartDays);

  const entriesByType = new Map<PersonSignalType, UserAlertEntry[]>();
  for (const entry of entries) {
    const type = personSignalTypeFor(entry.alertId);
    if (!type) continue;
    const grouped = entriesByType.get(type);
    if (grouped) grouped.push(entry);
    else entriesByType.set(type, [entry]);
  }

  const signals: FiredSignal[] = [];
  const dailyPointsByType: Partial<Record<PersonSignalType, DayPoint[]>> = {};
  const precomputedSeverities: Partial<Record<PersonSignalType, CardSeverity>> = {};
  entriesByType.forEach((typeEntries, type) => {
    const config = bandConfigByAlertId[type];
    if (!config) return;
    const allPoints = dayPointsForType(typeEntries);
    if (allPoints.length) {
      // The backend can send a weekday outside the range (e.g. the previous week's Sunday for a
      // Mon-Tue range) — with only a weekday name to go on it would resolve to the wrong week's date.
      const points = rangeDays.length ? allPoints.filter(point => rangeDays.includes(point.day)) : allPoints;
      if (!points.length) return;
      // Kept even if this type never breaches — AA-225's week-matrix needs its real daily values
      // for the unthresholded "Hours tracked" column.
      dailyPointsByType[type] = points;
      const signal = buildFiredSignalFromDayPoints(type, points, config, d, chartDays);
      if (signal) signals.push(signal);
      return;
    }
    const fallback = buildFiredSignalFromRangeAggregate(type, typeEntries, config, d, fallbackPeakDay);
    if (!fallback) return;
    signals.push(fallback.signal);
    precomputedSeverities[type] = fallback.severity;
  });
  return { signals, dailyPointsByType, precomputedSeverities };
}

/**
 * One column per elapsed company-chart weekday (`firstDayOfWeek` plus four, minus today and any later
 * day — the same days the precomputed cards draw, see buildPreviewWeekChart), so both flows show the
 * same week. One bar per fired signal type that has real per-day data anywhere on the card (a
 * range-aggregate-fallback-built type contributes no bar). A day with no real entry for a type
 * renders as hasData: false rather than guessed as breaching or not.
 *
 * `weekStart` is the company week's first date (companyWeekStartContaining), not the Monday anchor.
 * Columns are keyed by weekday name, not an exact date — a range spanning more than one week
 * would collapse same-named weekdays from different weeks into one column.
 */
export function buildPersonCardDailyChart(
  orderedTypes: PersonSignalType[], firedSignals: FiredSignal[], dailyPointsByType: Partial<Record<PersonSignalType, DayPoint[]>>,
  weekStart: Date, firstDayOfWeek = 1, timezone?: string, now?: DateTime,
): PersonCardDailyColumn[] {
  const peakDayByType = new Map(firedSignals.map(signal => [signal.type, signal.peakDay]));
  const typesWithRealData = orderedTypes.filter(type => (dailyPointsByType[type]?.length ?? 0) > 0);
  if (!typesWithRealData.length) return [];

  return elapsedChartWeekdays(isoDateFor(weekStart, 0), firstDayOfWeek, timezone, now).map(day => ({
    day,
    bars: typesWithRealData.map(type => {
      const point = dailyPointsByType[type]?.find(p => p.day === day);
      return {
        label: personSignalTypeShortLabel(type), breaching: point?.breaching ?? false, hasData: point != null,
        isPeak: peakDayByType.get(type) === day, displayValue: point ? formatPersonSignalValue(type, point.value) : '',
      };
    }),
  }));
}
